import { useMemo, useState } from 'react';
import { Alert, Button, CopyButton, Group, Select, Stack, Text, Textarea } from '@mantine/core';
import { CodeHighlight } from '@mantine/code-highlight';
import { CheckIcon, CopyIcon, DownloadSimpleIcon, WarningIcon } from '@phosphor-icons/react';
import { type Mode, convert, outputLang } from '@/lib/json-tools';
import { useStrings } from '@/i18n/useLang';

const SAMPLE = '{\n  "name": "ecosquirrel",\n  "tools": 12,\n  "local": true\n}';

function download(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function JsonTool() {
  const t = useStrings();
  const [mode, setMode] = useState<Mode>('format');
  const [input, setInput] = useState(SAMPLE);

  const result = useMemo(() => convert(mode, input), [mode, input]);
  const lang = outputLang(mode);
  const ext = lang === 'yaml' ? 'yaml' : lang === 'toml' ? 'toml' : mode === 'json2csv' ? 'csv' : 'json';

  return (
    <Stack gap="lg">
      <Select
        label={t.ui.mode}
        value={mode}
        onChange={(v) => v && setMode(v as Mode)}
        allowDeselect={false}
        data={[
          { value: 'format', label: t.ui.modeFormat },
          { value: 'minify', label: t.ui.modeMinify },
          { value: 'json2yaml', label: 'JSON → YAML' },
          { value: 'yaml2json', label: 'YAML → JSON' },
          { value: 'json2csv', label: 'JSON → CSV' },
          { value: 'csv2json', label: 'CSV → JSON' },
          { value: 'json2toml', label: 'JSON → TOML' },
          { value: 'toml2json', label: 'TOML → JSON' },
        ]}
        maw={260}
      />

      <Textarea
        label={t.ui.input}
        value={input}
        onChange={(e) => setInput(e.currentTarget.value)}
        autosize
        minRows={6}
        maxRows={18}
        styles={{ input: { fontFamily: 'monospace' } }}
      />

      {result.error && (
        <Alert color="red" icon={<WarningIcon size={18} />} variant="light">
          {result.error}
        </Alert>
      )}

      {result.output && (
        <div>
          <Group justify="space-between" mb={4}>
            <Text fw={600} size="sm">
              {t.ui.output}
            </Text>
            <Group gap="xs">
              <CopyButton value={result.output} timeout={1500}>
                {({ copied, copy }) => (
                  <Button variant="subtle" size="xs" leftSection={copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />} onClick={copy}>
                    {copied ? t.ui.copied : t.ui.copy}
                  </Button>
                )}
              </CopyButton>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<DownloadSimpleIcon size={14} />}
                onClick={() => download(`output.${ext}`, result.output)}
              >
                {t.ui.download}
              </Button>
            </Group>
          </Group>
          <CodeHighlight code={result.output} language={lang} className="code-wrap" />
        </div>
      )}
    </Stack>
  );
}
